import type { Client, UpdateClientCommand } from "@/domain/model/Client";
import type { ClientService } from "@/domain/port/ClientService";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type { ClientDto, CreateClientCommand } from "./dto/ClientDto";
import {
  clientFromDto,
  updateClientCmdToRequest,
} from "./mappers/ClientMapper";
import type { ClientId } from "@/domain/model/Ids";

const BATCH_LIMIT = 100;

export class ClientServiceImpl implements ClientService {
  constructor(private readonly http: HttpClient) {}

  async list(q: PageQuery): Promise<{ items: Client[]; total: number }> {
    const params = toClientQueryParams(q);

    const { items, total } = await this.http.request<{
      items: ClientDto[];
      total: number;
    }>(`/api/clients?${params.toString()}`);

    return { items: items.map(clientFromDto), total };
  }

  async create(cmd: CreateClientCommand): Promise<Client> {
    const res = await this.http.request<ClientDto>("/api/clients", {
      method: "POST",
      body: JSON.stringify(cmd),
    });

    return clientFromDto(res);
  }

  async getManyByIds(ids: ClientId[]): Promise<Client[]> {
    if (ids.length === 0) return [];

    // chunk to respect server max
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + BATCH_LIMIT));
    }

    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const qs = new URLSearchParams({ ids: chunk.join(",") }); // CSV as per your route
        const dtos = await this.http.request<ClientDto[]>(
          `/api/clients/_batch?${qs.toString()}`,
        );
        return dtos.map(clientFromDto);
      }),
    );

    return results.flat();
  }

  async getById(id: ClientId): Promise<Client> {
    const dto = await this.http.request<ClientDto>(
      `/api/clients/${encodeURIComponent(id)}`,
    );
    return clientFromDto(dto);
  }

  async update(id: ClientId, cmd: UpdateClientCommand): Promise<Client> {
    const req = updateClientCmdToRequest(cmd);
    const dto = await this.http.request<ClientDto>(
      `/api/clients/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        body: JSON.stringify(req),
      },
    );
    return clientFromDto(dto);
  }

  async delete(id: ClientId): Promise<Client> {
    const dto = await this.http.request<ClientDto>(
      `/api/clients/${encodeURIComponent(id)}`,
      {
        method: "DELETE",
      },
    );
    return clientFromDto(dto);
  }
}

function isNonEmptyString(x: unknown): x is string {
  return typeof x === "string" && x.trim().length > 0;
}

function flattenClientFilter(filter: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (isNonEmptyString(filter?.q)) out.q = filter.q.trim();
  return out;
}

function toClientQueryParams(q: PageQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(q.page),
    pageSize: String(q.pageSize),
    sortBy: q.sortBy ?? "createdAt",
    sortDir: q.sortDir ?? "desc",
  });

  const flat = flattenClientFilter(q.filter);
  for (const [k, v] of Object.entries(flat)) params.set(k, v);

  return params;
}
